<script lang="ts">
  import { onDestroy, untrack } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import { mdiInformationOutline } from "@mdi/js"
  import { formatEuroCents } from "./_shared/_logic/backlog-estimation"
  import TableStepLayout from "@kontextwerk/web-sdk/ui/layout/TableStepLayout.svelte"
  import type {
    ColumnDef,
    TableGetRows,
    TableRowsUpdater,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import PropertyUnitsQuickOverlay from "./_modules/property-units-quick-overlay"
  import type { QuickPropertyUnitSaveResult } from "./overlays/propertyUnitsQuickEdit/contract"
  import {
    applyBacklogPropertySelectionToLoadList,
    selectedPropertyIdsFromCandidates,
    selectionSourcesByPropertyIdFromCandidates,
  } from "./_shared/_logic/property-selection-selection"
  import { BATCH_UPLOAD_MAX_PROPERTIES } from "../../_shared/_logic/batch-upload-limits"
  import type {
    AddressCandidate,
    BatchUploadInit,
    BatchUploadWorkflowEvent,
    LoadListOutput,
    CreateWorkOrdersOutput,
    BatchUploadReferenceData,
    BatchUploadStepContext,
  } from "../../_shared/_logic/batch-upload-contract"
  import { normalizeSelectedBatchUnitIds } from "./_logic/planning-payloads"
  import BacklogListUploadAction from "./_modules/backlog-list-upload-action"
  import {
    getStableSortedRowPage,
    stableRowNumberColumn,
    stableRowNumberSort,
  } from "../../../../../../../../../../_shared/_logic/stable-row-numbers"
  import CompleteLoadListAction from "./_modules/complete-load-list-action"
  import type { CompleteLoadListResult } from "./actions/completeLoadList"
  import { buildCompleteLoadListIntent } from "./_actions/buildCompleteLoadListIntent"
  import BatchLoadListTable from "./_modules/batch-load-list-table"
  import { loadOpenWorkOrderTargets, resolveBacklogList } from "./load-list-commands"
  import type { BacklogListImportOptions } from "../../../../../../../../../../../../api/kehrblatt/backlog-lists"
  import {
    loadMissingPlanningUnitContactContexts,
    planningUnitContactTargetsForProperties,
  } from "../../../../../../../../../../_shared/_logic/planning-contact-contexts"
  import { filterAddressRows, openWorkOrderPropertyIdsForRows } from "./_shared/_logic/table-model"
  import {
    buildOpenWorkOrderWarningMap,
    type OpenWorkOrderWarningMap,
  } from "./_logic/openWorkOrderWarnings"
  import {
    backlogUnitIdsByPropertyId,
    exactBacklogUnitIds,
    mergeBacklogUnitSelections,
    backlogAddressUnitId,
  } from "./_shared/_logic/unit-selection"
  import { isBuildingPropertyUnit } from "../../../../../../../../../../_shared/_logic/units"
  import { KEEP_PRECONFIGURATION_VALUE } from "../../../../../../../../../../_shared/_logic/preconfiguration-keep"
  import { createLoadListState, type LoadListState } from "./_logic/loadListState.svelte"
  import { loadListRowReferences, projectLoadListRows } from "./_logic/loadListRows"
  import type { LoadListTableRow } from "./_shared/_logic/load-list-row-model"
  import RowDetailsDialog, { buildRowDetails } from "./_modules/row-details-dialog"

  import { persistPreparedWorkOrders } from "./load-list-commands"
  import WorkOrderFieldCell from "./_modules/work-order-field-cell"
  import OrderFieldsPreconfiguration, {
    orderFieldsPatchFromPreconfiguration,
    orderFieldsPreconfigurationFromFields,
  } from "./_modules/order-fields-preconfiguration"
  import {
    copyCreationFields,
    defaultCreationFields,
    validateCreationFieldsPatch,
  } from "../../_shared/_logic/work-order-creation"
  import { planningDueDatePresetOptions } from "../../_shared/_logic/planning-due-date-preset"
  import BatchApplyActionBox from "../../../../../../../../../../_shared/_modules/batch-apply-action-box"

  type Props = {
    ctx: BatchUploadStepContext
    init: BatchUploadInit
    output?: CreateWorkOrdersOutput
    referenceData: BatchUploadReferenceData
    loadListState?: LoadListState
    locked?: boolean
    readOnly?: boolean
    onEvent: (event: BatchUploadWorkflowEvent) => void
  }
  const {
    ctx,
    init,
    referenceData,
    output,
    loadListState,
    locked: stepLocked = false,
    readOnly = false,
    onEvent,
  }: Props = $props()
  const controller = untrack(
    () =>
      loadListState ??
      createLoadListState({
        organizationId: init.organizationId,
        properties: init.properties,
        output,
      }),
  )
  const data = controller.state
  const hasPlanningRows = $derived(data.rows.length > 0)
  controller.initializeDefaults(
    untrack(() =>
      defaultCreationFields(
        init.orderTypes,
        referenceData.employeeOptions.map((item) => String(item.value)),
        referenceData.planningSettings?.timezone,
      ),
    ),
  )
  controller.configureOrderTypes(untrack(() => init.orderTypes))
  // Rows start from the creation defaults, so the concrete defaults leave the table unchanged.
  let pendingOrderPreconfiguration = $state(
    orderFieldsPreconfigurationFromFields(
      untrack(() => data.creationDefaults!),
      planningDueDatePresetOptions(
        new Date(),
        untrack(() => referenceData.planningSettings?.timezone),
      ),
    ),
  )
  const keepImportedOrderFields = () => {
    const backlog = data.backlogListImport

    if (!backlog) return

    if (backlog.options?.mode === "manual") {
      const fixedDuration =
        backlog.options.contract_version === 1
          ? backlog.options.duration_minutes
          : backlog.options.duration_strategy.method === "fixed"
            ? backlog.options.duration_strategy.duration_minutes
            : null
      const preset = orderFieldsPreconfigurationFromFields(
        {
          ...data.creationDefaults!,
          order_type_ids: backlog.options.order_type_ids,
          estimated_duration_min: fixedDuration !== null ? String(fixedDuration) : "",
          custom_order_type: null,
        },
        [],
      )
      pendingOrderPreconfiguration = {
        ...pendingOrderPreconfiguration,
        orderTypeValues: preset.orderTypeValues,
        customOrderTypeLabel: "",
        duration: fixedDuration !== null ? preset.duration : KEEP_PRECONFIGURATION_VALUE,
        customDurationMinutes: fixedDuration !== null ? preset.customDurationMinutes : "",
      }

      return
    }

    if (
      !backlog.options &&
      (!["portal_uno_office", "portal_kamin_futura"].includes(backlog.sourceSystem) ||
        !backlog.rows.some((row) => row.suggested_order_type_ids?.length))
    )
      return

    pendingOrderPreconfiguration = {
      ...pendingOrderPreconfiguration,
      orderTypeValues: [KEEP_PRECONFIGURATION_VALUE],
      customOrderTypeLabel: "",
      duration: KEEP_PRECONFIGURATION_VALUE,
      customDurationMinutes: "",
    }
  }
  keepImportedOrderFields()
  let selectedIds = $state<string[]>([])
  let backlogRequestId = 0
  const locked = $derived(stepLocked || Boolean(output) || Boolean(data.frozenSubmission))
  const creationTargets = $derived(
    data.rows.filter(
      (row) => !row.removed && (!selectedIds.length || selectedIds.includes(row.id)),
    ),
  )
  const applyOrderFields = () => {
    const patch = orderFieldsPatchFromPreconfiguration(pendingOrderPreconfiguration)
    const error = validateCreationFieldsPatch(patch)

    if (error) {
      ctx.setError(error)

      return
    }

    if (locked || readOnly) return

    controller.applyOrderFields(patch, selectedIds)
    changed()
  }
  const orderFieldsByRowId = $derived(new Map(data.rows.map((row) => [row.id, row.orderFields])))
  const changeOrderFields = (
    rowId: string,
    value: import("../../_shared/_logic/work-order-creation").WorkOrderCreationFields,
  ) => {
    if (locked || readOnly) return

    const error = controller.saveOrderFields(rowId, value)

    ctx.setError(error)
  }
  let alive = true
  let contactContextRequestId = 0
  let warningRefreshRequestId = 0
  let contactContextLoadFailed = $state(false)
  let openWorkOrderWarningsByTarget = $state<OpenWorkOrderWarningMap>({})
  let loadedOpenWorkOrderPropertyIds = $state<string[]>([])
  let failedOpenWorkOrderPropertyIds = $state<string[]>([])
  let loadingOpenWorkOrderPropertyIds = $state<string[]>([])
  let loadedAddressRowIds = $state<string[]>([])
  let detailRowId = $state<string | null>(null)
  let focusCalculation = $state(false)
  const estimatesByRowId = $derived(controller.estimatesByRowId())
  const hasPrices = $derived(
    Array.from(estimatesByRowId.values()).some((estimate) => estimate.priceCents !== null),
  )
  let refreshTable: (() => void | Promise<void>) | null = null
  let updateTableRows: TableRowsUpdater | null = null
  let addressTableSort = stableRowNumberSort
  const propertyLookup = $derived(
    new Map(data.properties.map((property) => [property.id, property])),
  )
  const selectedUnitIdsByPropertyId = $derived(controller.selectedUnitIdsByPropertyId())
  const activeDrafts = $derived(data.rows.filter((row) => !row.removed))
  const activeSelectedPropertyIds = $derived(
    Array.from(new Set(activeDrafts.flatMap((row) => (row.propertyId ? [row.propertyId] : [])))),
  )
  const activeSelectedPropertyIdSet = $derived(new Set(activeSelectedPropertyIds))
  const selectionSourcesByPropertyId = $derived.by(() => {
    const candidatesByKey = new Map(data.candidates.map((candidate) => [candidate.key, candidate]))

    return selectionSourcesByPropertyIdFromCandidates([
      ...data.candidates.filter(
        (candidate) =>
          candidate.propertyId && activeSelectedPropertyIdSet.has(candidate.propertyId),
      ),
      ...activeDrafts.flatMap((row) => {
        const candidate = candidatesByKey.get(row.sourceCandidateKey)

        return candidate ? [{ ...candidate, propertyId: row.propertyId }] : []
      }),
    ])
  })
  const addressRows = $derived(
    projectLoadListRows({
      rows: data.rows,
      candidates: data.candidates,
      properties: data.properties,
      contactContexts: data.unitContactContexts,
      contextLoadFailed: contactContextLoadFailed,
      warningsByTarget: openWorkOrderWarningsByTarget,
      loadedWarningPropertyIds: loadedOpenWorkOrderPropertyIds,
      loadingWarningPropertyIds: loadingOpenWorkOrderPropertyIds,
      failedWarningPropertyIds: failedOpenWorkOrderPropertyIds,
    }),
  )
  const activeAddressRows = $derived(addressRows.filter((row) => !row.removed))
  const detailRow = $derived(addressRows.find((row) => row.id === detailRowId) ?? null)
  const detailDraft = $derived(data.rows.find((row) => row.id === detailRowId) ?? null)
  const detailSources = $derived(
    new Map((data.backlogListImport?.rows ?? []).map((row) => [row.source_row_id, row])),
  )
  const rowDetails = $derived(
    detailRow && detailDraft
      ? buildRowDetails({
          row: detailRow,
          draft: detailDraft,
          backlog: data.backlogListImport,
          sourcesById: detailSources,
          orderTypes: init.orderTypes,
          estimate: estimatesByRowId.get(detailRow.id),
        })
      : null,
  )
  const backlogOpenCount = $derived(
    activeAddressRows.filter((row) => row.attentionReason === "backlog_unresolved").length,
  )
  const pendingUnitRows = $derived(activeAddressRows.filter((row) => row.needsUnitSelection))
  const missingRows = $derived(
    activeAddressRows.filter((row) => row.attentionReason === "address_missing"),
  )
  const addressTableRows = $derived(
    filterAddressRows({
      rows: addressRows,
      filters: data.filters,
    }),
  )
  const completeLoadListIntent = $derived(
    buildCompleteLoadListIntent({
      locked: stepLocked || Boolean(output),
      existingOutput: output,
      unresolvedBacklogCount: backlogOpenCount,
      missingDbCount: missingRows.length,
      pendingUnitSelectionCount: pendingUnitRows.length,
      pendingUnitSelectionReferences: loadListRowReferences(pendingUnitRows),
      trialRestrictionsActive: init.trialRestrictionsActive,
      planningRowCount: activeDrafts.length,
      outputInput: {
        propertyPool: data.properties,
        selectedPropertyIds: activeSelectedPropertyIds,
        selectedUnitIdsByPropertyId,
        importedFromList: Boolean(data.backlogListImport),
        selectionSourcesByPropertyId,
        backlogListImport: data.backlogListImport,
        unitContactContexts: data.unitContactContexts,
        selectedTargets: activeDrafts.flatMap((row) =>
          row.propertyId && row.selectedUnitId
            ? [
                {
                  rowId: row.id,
                  propertyId: row.propertyId,
                  propertyUnitId: row.selectedUnitId,
                  orderFields: copyCreationFields(row.orderFields),
                  planningOrigin: {
                    manualOrderTypes: row.manualOrderTypes,
                    manualDuration: row.manualDuration,
                    workTypesFromImport: row.workTypesFromImport,
                    workSourceCopied: row.workSourceCopied,
                    workSourceRowIds: row.workSourceRowIds,
                    workTargetSelectedManually: row.workTargetSelectedManually,
                  },
                },
              ]
            : [],
        ),
      },
    }),
  )
  const tableColumns = $derived<ColumnDef[]>([
    { ...stableRowNumberColumn(), width: "110px" },
    { id: "address", label: "Liegenschaft", isPrimary: true, width: "270px" },
    { id: "unit", label: "Einheit", width: "220px" },
    { id: "order", label: "Auftragstyp", width: "320px" },
    ...(hasPrices ? [{ id: "price", label: "Preis", width: "140px" }] : []),
    { id: "duration", label: "Dauer", width: "135px" },
    { id: "employees", label: "Zugelassen", width: "260px" },
    { id: "warning", label: "Hinweise", width: "170px" },
  ])
  const getAddressRows: TableGetRows = (params) => {
    addressTableSort = params.sort ?? stableRowNumberSort

    return getStableSortedRowPage({
      rows: addressTableRows,
      columns: tableColumns,
      sort: addressTableSort,
      limit: Math.max(1, addressTableRows.length),
      offset: 0,
    })
  }
  const syncTableRows = () => {
    if (!alive) return

    const result = getStableSortedRowPage({
      rows: addressTableRows,
      columns: tableColumns,
      sort: addressTableSort,
      limit: Math.max(1, addressTableRows.length),
      offset: 0,
    })

    if (updateTableRows) updateTableRows(() => result.rows, { total: result.total })
    else void refreshTable?.()
  }
  const requestTableRowSync = () => queueMicrotask(syncTableRows)
  $effect(() => {
    addressTableRows
    untrack(requestTableRowSync)
  })

  const refreshContactContexts = async () => {
    const targets = planningUnitContactTargetsForProperties(data.properties)

    if (!targets.length || !init.organizationId) return

    const requestId = ++contactContextRequestId
    contactContextLoadFailed = false
    try {
      const contexts = await loadMissingPlanningUnitContactContexts({
        organizationId: init.organizationId,
        targets,
        existing: data.unitContactContexts,
      })

      if (!alive || requestId !== contactContextRequestId) return

      data.unitContactContexts = contexts
    } catch {
      if (alive && requestId === contactContextRequestId) contactContextLoadFailed = true
    }
  }
  $effect(() => {
    data.properties
    untrack(() => {
      void refreshContactContexts()
    })
  })

  const markWarningsStale = (propertyIds: string[]) => {
    const ids = new Set(propertyIds)
    loadedOpenWorkOrderPropertyIds = loadedOpenWorkOrderPropertyIds.filter((id) => !ids.has(id))
    failedOpenWorkOrderPropertyIds = failedOpenWorkOrderPropertyIds.filter((id) => !ids.has(id))
  }
  const refreshOpenWorkOrderWarnings = async () => {
    const loadedRowIds = new Set(loadedAddressRowIds)
    const propertyIds = openWorkOrderPropertyIdsForRows({
      rows: addressRows.filter((row) => loadedRowIds.has(row.id)),
      loadedPropertyIds: loadedOpenWorkOrderPropertyIds,
      loadingPropertyIds: loadingOpenWorkOrderPropertyIds,
    })

    if (!propertyIds.length || !init.organizationId) return

    const generation = warningRefreshRequestId
    const requestedPropertyIds = new Set(propertyIds)
    loadingOpenWorkOrderPropertyIds = [...loadingOpenWorkOrderPropertyIds, ...propertyIds]
    try {
      const orders = await loadOpenWorkOrderTargets({
        organizationId: init.organizationId,
        propertyIds,
      })

      if (!alive || generation !== warningRefreshRequestId) return

      failedOpenWorkOrderPropertyIds = failedOpenWorkOrderPropertyIds.filter(
        (id) => !requestedPropertyIds.has(id),
      )

      const keys = new Set(
        propertyIds.flatMap((id) => [
          `property:${id}`,
          ...(propertyLookup.get(id)?.units ?? []).map((unit) => `unit:${unit.id}`),
        ]),
      )
      openWorkOrderWarningsByTarget = {
        ...Object.fromEntries(
          Object.entries(openWorkOrderWarningsByTarget).filter(([key]) => !keys.has(key)),
        ),
        ...buildOpenWorkOrderWarningMap(orders),
      }
      loadedOpenWorkOrderPropertyIds = Array.from(
        new Set([...loadedOpenWorkOrderPropertyIds, ...propertyIds]),
      )
    } catch {
      if (alive && generation === warningRefreshRequestId) {
        failedOpenWorkOrderPropertyIds = Array.from(
          new Set([...failedOpenWorkOrderPropertyIds, ...propertyIds]),
        )
        loadedOpenWorkOrderPropertyIds = Array.from(
          new Set([...loadedOpenWorkOrderPropertyIds, ...propertyIds]),
        )
      }
    } finally {
      if (alive && generation === warningRefreshRequestId) {
        loadingOpenWorkOrderPropertyIds = loadingOpenWorkOrderPropertyIds.filter(
          (id) => !requestedPropertyIds.has(id),
        )
      }
    }
  }
  const changed = () => {
    ctx.setError?.(null)
    requestTableRowSync()
    queueMicrotask(() => {
      void refreshOpenWorkOrderWarnings()
    })
  }
  const copyRow = (row: LoadListTableRow) => {
    if (locked || readOnly) return

    const error = controller.copyRow(row.id, addressTableSort?.dir === "desc")

    if (error) newNotification({ class: "info", html: error })

    changed()
  }
  const selectUnit = (row: LoadListTableRow, unitId: string | null) => {
    if (locked || readOnly) return

    const error = controller.selectUnit(row.id, unitId)

    if (error) newNotification({ class: "error", html: error })

    changed()
  }
  const setRowsRemoved = (rows: LoadListTableRow[], removed: boolean) => {
    if (locked || readOnly) return

    const conflicts = controller.setRemoved(
      rows.map((row) => row.id),
      removed,
    )

    if (conflicts.length) newNotification({ class: "warning", html: conflicts[0] })

    changed()
  }
  const initialUnitIdsFor = (
    candidate: AddressCandidate | undefined,
    property: Property,
  ): string[] => {
    if (candidate?.backlogResolution?.matchReason === "number_address_conflict") return []

    if (
      candidate?.backlogResolution &&
      data.backlogListImport?.sourceSystem === "portal_uno_office"
    ) {
      return exactBacklogUnitIds(property, candidate.backlogResolution.customerNumber)
    }

    if (
      candidate?.backlogResolution?.address != null &&
      ["portal_kamin_futura", "portal_dexheimer_agzess", "isi_export"].includes(
        data.backlogListImport?.sourceSystem ?? "",
      )
    ) {
      const id = backlogAddressUnitId(property, data.backlogListImport!.sourceSystem)

      return id ? [id] : []
    }

    return normalizeSelectedBatchUnitIds(property)
  }
  const changeAddress = (row: LoadListTableRow, property: Property | null) => {
    if (locked || readOnly || row.removed) return

    const candidate = data.candidates.find((item) => item.key === row.key)
    controller.changeProperty(
      row.id,
      property,
      property ? initialUnitIdsFor(candidate, property) : [],
    )

    if (
      candidate &&
      data.rows.filter((item) => item.sourceCandidateKey === candidate.key).length === 1
    ) {
      candidate.propertyId = property?.id ?? null
    }

    markWarningsStale([row.propertyId, property?.id].filter((id): id is string => Boolean(id)))
    changed()
  }
  const handleUnitQuickOverlaySaved = (result: QuickPropertyUnitSaveResult) => {
    if (!result.property) return

    data.properties = [
      ...data.properties.filter((item) => item.id !== result.property.id),
      result.property,
    ]
    const valid = new Set((result.property.units ?? []).map((unit) => String(unit.id)))
    for (const row of data.rows.filter((item) => item.propertyId === result.property.id)) {
      if (row.selectedUnitId && !valid.has(row.selectedUnitId)) row.selectedUnitId = null
    }
    markWarningsStale([result.property.id])
    changed()
  }
  const editUnits = (row: LoadListTableRow) => {
    const property = propertyLookup.get(row.propertyId ?? "")

    if (locked || readOnly || !property) return

    openOverlay({
      title: "Wohneinheiten bearbeiten",
      content: PropertyUnitsQuickOverlay,
      closeOnParentOpen: false,
      properties: {
        property,
        selectedUnitIds: selectedUnitIdsByPropertyId[property.id] ?? [],
        onSaved: handleUnitQuickOverlaySaved,
      },
    })
  }
  const mergeInput = () => ({
    localProperties: data.properties,
    selectedPropertyIds: selectedPropertyIdsFromCandidates(data.candidates),
    addressCandidates: data.candidates,
  })
  const handleBacklogListImport = async (
    file: File,
    options: BacklogListImportOptions,
    signal: AbortSignal,
  ) => {
    if (locked || readOnly || !init.organizationId) return false

    const requestId = ++backlogRequestId
    const organizationId = init.organizationId

    const result = await resolveBacklogList({
      organizationId: init.organizationId,
      seedProperties: data.properties,
      options,
      signal,
      file,
    })

    if (
      !alive ||
      !result ||
      signal.aborted ||
      organizationId !== init.organizationId ||
      locked ||
      readOnly ||
      requestId !== backlogRequestId
    )
      return false

    const previous = data.backlogListImport
      ? backlogUnitIdsByPropertyId({
          properties: data.properties,
          rows: data.backlogListImport.rows,
          sourceSystem: data.backlogListImport.sourceSystem,
        })
      : {}
    const merged = applyBacklogPropertySelectionToLoadList({
      ...mergeInput(),
      backlogProperties: result.properties,
      backlogCandidates: result.candidates,
    })
    const selections = mergeBacklogUnitSelections({
      current: controller.selectedUnitIdsByPropertyId(),
      previousBacklog: previous,
      incomingBacklog: result.selectedUnitIdsByPropertyId,
      removedPropertyIds: merged.removedPropertyIds,
      manualPropertyIds: data.rows
        .filter((row) => row.workTargetSelectedManually && !row.removed && row.propertyId)
        .map((row) => row.propertyId!),
    })
    data.properties = merged.localProperties
    controller.reconcileCandidates(merged.addressCandidates, selections)
    controller.applyUnitSelections(
      selections,
      Array.from(
        new Set([...Object.keys(previous), ...Object.keys(result.selectedUnitIdsByPropertyId)]),
      ),
    )
    controller.setBacklogListImport(result.backlogListImport)
    keepImportedOrderFields()
    markWarningsStale([...merged.selectedPropertyIds, ...merged.removedPropertyIds])
    changed()

    return true
  }
  const handleComplete = async (result: CompleteLoadListResult) => {
    if (result.kind === "completed") {
      const created = await persistPreparedWorkOrders(controller, result.output)
      await init.onWorkflowMutated(created.createdRows.map((row) => row.workOrder))

      if (!alive) return

      await onEvent({ type: "load_list_completed", output: created })
    }

    if (alive) await ctx.requestNext?.()
  }
  onDestroy(() => {
    alive = false

    contactContextRequestId += 1
    warningRefreshRequestId += 1
  })
  const numberFormatter = new Intl.NumberFormat("de-DE")
  const title = $derived(
    `Aufträge vorbereiten (${numberFormatter.format(addressTableRows.length)}${addressTableRows.length !== addressRows.length ? ` von ${numberFormatter.format(addressRows.length)}` : ""})`,
  )
</script>

{#snippet orderCell(row: LoadListTableRow, column: string)}
    {@const fields = orderFieldsByRowId.get(row.id)}
    {@const estimate = estimatesByRowId.get(row.id)}
    {#if fields}
        {#if column === "price"}
            <div
        class="list-price"
        title={estimate?.totalPrice
          ? "Gesamtpreis der ursprünglichen UNO-Listenzeile; nicht auf einzelne Arbeiten aufteilbar."
          : "Summe der bepreisten, aktuell ausgewählten Arbeiten aus der Liste."}
      >
                <span
          >{estimate?.priceCents == null ? "Unbekannt" : formatEuroCents(estimate.priceCents)}</span
        >
            </div>
        {:else}
            <div class="order-field-container" class:duration-cell={column === "duration"}>
                <WorkOrderFieldCell
          {column}
          value={fields}
          rowId={row.id}
          rowNumber={row.rowNumber}
          orderTypes={init.orderTypes}
          employeeOptions={referenceData.employeeOptions}
          disabled={locked || readOnly || row.removed}
          onChange={(value) => changeOrderFields(row.id, value)}
        />
                {#if column === "duration"}
                    <CTA
            type="tableButton"
            icon={mdiInformationOutline}
            small={true}
            ariaLabel="Preis und Dauer erklären"
            tooltipContent="Preis und Dauer erklären"
            onClick={() => {
              focusCalculation = true
              detailRowId = row.id
            }}
          />
                {/if}
            </div>
        {/if}
    {/if}
{/snippet}

{#if !hasPlanningRows && !readOnly}
  <BacklogListUploadAction
    organizationId={init.organizationId}
    orderTypes={init.orderTypes}
    disabled={locked}
    onConfirm={handleBacklogListImport}
  />
{:else}
<CompleteLoadListAction intent={completeLoadListIntent} onSubmissionResult={handleComplete}>
    {#snippet children(primaryAction)}
        <TableStepLayout
        titleActions={readOnly ? null : primaryAction}
        {title}
        darkMode={true}
        showPreconfiguration={!readOnly}
        showFooter={!readOnly}
        helpModalTextId="workOrderBatchLoadList"
      >
            {#snippet preconfiguration()}
                <div class="load-list-preconfiguration">
                    {#if !locked}<BatchApplyActionBox
                darkMode={false}
                selectedCount={selectedIds.length}
                targetCount={creationTargets.length}
                disabled={readOnly || locked}
                onApply={applyOrderFields}
              >
                            <OrderFieldsPreconfiguration
                  brightBackground={false}
                  bind:value={pendingOrderPreconfiguration}
                  orderTypes={init.orderTypes}
                  employeeOptions={referenceData.employeeOptions}
                  timezone={referenceData.planningSettings?.timezone}
                />
                        </BatchApplyActionBox>{/if}
                </div>
            {/snippet}
            {#snippet footer()}{@render primaryAction()}{/snippet}

            <BatchLoadListTable
          columns={tableColumns}
          getRows={getAddressRows}
          defaultSort={stableRowNumberSort}
          pageSize={Math.max(1, addressRows.length)}
          refreshKey={JSON.stringify(data.filters)}
          filters={data.filters}
          onFiltersChange={(filters) => {
            data.filters = filters
          }}
          {locked}
          {readOnly}
          organizationId={init.organizationId}
          seedProperties={data.properties}
          propertyForId={(id) => propertyLookup.get(id) ?? null}
          onSetRefreshFunction={(fn) => {
            refreshTable = fn
            requestTableRowSync()
          }}
          onSetRowsUpdater={(updater) => {
            updateTableRows = updater
          }}
          onRowsLoaded={(rows) => {
            const ids = rows.map((row) => row.id)

            if (ids.join("|") !== loadedAddressRowIds.join("|")) loadedAddressRowIds = ids

            queueMicrotask(() => {
              void refreshOpenWorkOrderWarnings()
            })
          }}
          onUnitSelectionChange={selectUnit}
          onAddressChange={changeAddress}
          onEditUnits={editUnits}
          onSelectionChange={(context) => (selectedIds = context.selectedIds.map(String))}
          {orderCell}
          onCopyRow={copyRow}
          onShowDetails={(row) => {
            focusCalculation = false
            detailRowId = row.id
          }}
          onRemoveRow={(row) => setRowsRemoved([row], true)}
          onRestoreRow={(row) => setRowsRemoved([row], false)}
          onRemoveRows={(rows) => setRowsRemoved(rows, true)}
          onRestoreRows={(rows) => setRowsRemoved(rows, false)}
        />
        </TableStepLayout>
    {/snippet}
</CompleteLoadListAction>
{/if}
{#if rowDetails}
    <RowDetailsDialog
    details={rowDetails}
    {focusCalculation}
    onUseCalculatedDuration={!readOnly &&
    !locked &&
    !detailDraft?.removed &&
    detailDraft?.manualDuration
      ? () => {
          if (detailRowId) controller.useCalculatedDuration(detailRowId)

          changed()
        }
      : undefined}
    onClose={() => {
      detailRowId = null
    }}
  />
{/if}
<style>
  .list-price {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    width: 100%;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .order-field-container {
    width: 100%;
    min-width: 0;
  }
  .duration-cell {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 28px;
    align-items: center;
    gap: 2px;
    min-width: 0;
  }
  .load-list-preconfiguration {
    display: grid;
    gap: 1.5rem;
    min-width: 0;
  }
</style>
